import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  CogIcon,
  UserIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  TagIcon,
  TicketIcon,
} from "@heroicons/react/24/outline";

import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import Notification from "../components/Notification";
import {
  SettingsTabLink,
  SettingsTabOption,
} from "../components/settings/SettingsTabLink";

const settings: Array<{
  id: string;
  name: string;
  icon: typeof UserIcon;
  path: string;
}> = [
  { id: "profile", name: "Profile", icon: UserIcon, path: "profile" },
  { id: "general", name: "General", icon: CogIcon, path: "general" },
  { id: "billing", name: "Billing", icon: CreditCardIcon, path: "billing" },
  { id: "security", name: "Security", icon: ShieldCheckIcon, path: "security" },
  {
    id: "integrations",
    name: "Integrations",
    icon: AdjustmentsHorizontalIcon,
    path: "integrations",
  },
  { id: "sync", name: "Sync", icon: ArrowPathIcon, path: "sync" },
  {
    id: "family-discounts",
    name: "Family Discounts & ID's",
    icon: TagIcon,
    path: "family-discounts",
  },
  {
    id: "discount-codes",
    name: "Discount Codes",
    icon: TicketIcon,
    path: "discount-codes",
  },
];

export default function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();

  // Extract the active tab from the current path
  const pathSegments = location.pathname.split("/");
  const lastSegment = pathSegments[pathSegments.length - 1];
  const activeTab =
    settings.find((tab) => tab.path === lastSegment)?.id || "profile";

  const { studioUser } = useStudioUser();
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    title: "",
  });

  return (
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6 min-w-0 max-w-full">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Settings
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage your studio settings and preferences
            </p>
          </div>

          <div className="sm:hidden">
            <label htmlFor="tabs" className="sr-only">
              Select a tab
            </label>
            <select
              id="tabs"
              name="tabs"
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
              value={activeTab}
              onChange={(e) => {
                const selected = settings.find(
                  (tab) => tab.id === e.target.value
                );
                if (selected) navigate(`/app/settings/${selected.path}`);
              }}
            >
              {settings.map((tab) => (
                <SettingsTabOption key={tab.id} tab={tab} />
              ))}
            </select>
          </div>
          <div className="hidden sm:block">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {settings.map((tab) => (
                  <SettingsTabLink
                    key={tab.id}
                    tab={tab}
                    isActive={activeTab === tab.id}
                  />
                ))}
              </nav>
            </div>
          </div>

          <div>
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}